import './reanimatedConfig';
// import 'react-native-gesture-handler';
import React, { useEffect } from 'react';
import NetInfo from '@react-native-community/netinfo';
import { ExtrasContextProvider } from './Context/ExtrasContext';
import { ModalContextProvider } from './Context/modalContext';
import { SearchContextProvider } from './Context/SearchContext';
import AuthProvider from './Context/AuthContext';
import Persistlogin from './utils/PersistLogin';
import { Provider } from 'react-redux';
import store, { persistor } from './redux/Store';
// import DropdownContextProvider from "./Context/DropdownContext";
import { PersistGate } from 'redux-persist/integration/react';
import Body from './Body';
import { PrintReceiptContextProvider } from './Context/PrintReceiptContext';
import { ErrorContextProvider } from './Context/ErrorContext';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { ProductContextProvider } from './Context/ProductContext';
import { NavigationProvider } from './Context/NavigationContext';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { LogBox, Platform, View } from 'react-native';
import { LayoutSizeProvider, ThemeProvider } from './theme';
import {
  Provider as PaperProvider,
  MD3LightTheme,
  MD3DarkTheme,
} from 'react-native-paper';
import { useTheme } from './theme';
// import { normalizeSize } from './utils/libs';
import NotificationHandler from './firebase/NotificationHandler';
// import NotificationHandler from './firebase/NotificationHandler';
import ReviewService from './services/ReviewService';
import { ANDROID_PACKAGE_NAME, IOS_APPSTORE_ID } from './CONSTANTS';
import AppAlertHost from './components/Reusables/AppAlertHost';
import AppSnackbarHost from './components/Reusables/AppSnackbarHost';
import RatingPromptHost from './components/Reusables/RatingPromptHost';
import ElectronAboutModal from './components/web/ElectronAboutModal';
import { HeaderTooltipProvider } from './components/Reusables/headers/HeaderTooltipProvider';
import { startPreAuthImagePreload } from './utils/preloadPreAuthImages';
import ChatwootProvider from './components/support/ChatwootProvider';
import AxiosInterceptorBootstrap from './components/bootstrap/AxiosInterceptorBootstrap';
import RealtimeStreamsController from './components/realtime/RealtimeStreamsController';
import NotificationsSyncController from './components/notifications/NotificationsSyncController';

// In Electron, NetInfo defaults to reachabilityUrl: '/' which resolves to file:/// under file protocol.
// Google's endpoint blocks CORS. Use GitHub API which allows cross-origin.
const isElectron =
  typeof navigator !== 'undefined' &&
  navigator?.userAgent?.toLowerCase().includes('electron');
if (Platform.OS === 'web' && isElectron) {
  NetInfo.configure({
    reachabilityUrl: 'https://clients3.google.com/generate_204',
    reachabilityTest: async response => response.status === 204,
  });
}

function App() {
  const isWeb = Platform.OS === 'web';

  useEffect(() => {
    if (Platform.OS === 'ios' && IOS_APPSTORE_ID) {
      ReviewService.setIOSAppStoreId(IOS_APPSTORE_ID);
    }
    ReviewService.setAndroidPackageName(ANDROID_PACKAGE_NAME);
  }, []);

  useEffect(() => {
    startPreAuthImagePreload();
  }, []);

  // IMPORTANT: don't silence console on web/dev. It hides real runtime errors and looks like a "white screen with no errors".
  if (!__DEV__ && !isWeb) {
    LogBox.ignoreAllLogs();
    console.log = () => {};
    console.warn = () => {};
    console.error = () => {};
  }

  return (
    <SafeAreaProvider>
      {!isWeb ? <NotificationHandler /> : null}
      <GestureHandlerRootView style={{ flex: 1 }}>
        <Provider store={store}>
          <ThemeProvider>
            <LayoutSizeProvider>
              <PaperThemeBridge>
                <AuthProvider>
                  <ExtrasContextProvider>
                    <ErrorContextProvider>
                      <BottomSheetModalProvider>
                        <PersistGate loading={null} persistor={persistor}>
                          <AxiosInterceptorBootstrap />
                          <RealtimeStreamsController />
                          <NotificationsSyncController />
                          <ModalContextProvider>
                            <ProductContextProvider>
                              <SearchContextProvider>
                                <NavigationProvider>
                                  <Persistlogin>
                                    <PrintReceiptContextProvider>
                                      <KeyboardProvider>
                                        <ChatwootProvider>
                                          <Body />
                                        </ChatwootProvider>
                                      </KeyboardProvider>
                                    </PrintReceiptContextProvider>
                                  </Persistlogin>
                                </NavigationProvider>
                              </SearchContextProvider>
                            </ProductContextProvider>
                          </ModalContextProvider>
                        </PersistGate>
                      </BottomSheetModalProvider>
                    </ErrorContextProvider>
                  </ExtrasContextProvider>
                </AuthProvider>
              </PaperThemeBridge>
            </LayoutSizeProvider>
          </ThemeProvider>
        </Provider>
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
}

const PaperThemeBridge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { theme, isDark } = useTheme();
  const base = isDark ? MD3DarkTheme : MD3LightTheme;

  useEffect(() => {
    ensureWebVectorIconFonts();
  }, []);

  const paperTheme = {
    ...base,
    roundness: theme.borderRadius.md,
    colors: {
      ...base.colors,
      primary: theme.colors.primary,
      background: theme.colors.background,
      surface: theme.colors.surface,
      surfaceVariant: theme.colors.surfaceSecondary,
      onSurface: theme.colors.text,
      onSurfaceVariant: theme.colors.textSecondary,
      outline: theme.colors.borderGray,
      error: theme.colors.error,
    },
  } as typeof base;
  return (
    <PaperProvider theme={paperTheme}>
      <AppAlertHost useCustomAlerts={Platform.OS === 'web'} />
      <AppSnackbarHost />
      <RatingPromptHost />
      <ElectronAboutModal />
      <HeaderTooltipProvider>{children}</HeaderTooltipProvider>
    </PaperProvider>
  );
};

function ensureWebVectorIconFonts() {
  if (Platform.OS !== 'web') return;

  const doc: any = (globalThis as any)?.document;
  if (!doc?.head) return;

  const styleId = 'rn-vector-icons-fonts';
  if (doc.getElementById(styleId)) return;

  // Webpack may return either a string or { default: string } depending on loader config.
  const url = (mod: any) =>
    typeof mod === 'string' ? mod : mod?.default || '';

  const css = `
@font-face {
  font-family: 'MaterialDesignIcons';
  src: url(${url(require('@react-native-vector-icons/material-design-icons/fonts/MaterialDesignIcons.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'MaterialIcons-Regular';
  src: url(${url(require('@react-native-vector-icons/material-icons/fonts/MaterialIcons.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'Ionicons';
  src: url(${url(require('@react-native-vector-icons/ionicons/fonts/Ionicons.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'Feather';
  src: url(${url(require('@react-native-vector-icons/feather/fonts/Feather.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'Entypo';
  src: url(${url(require('@react-native-vector-icons/entypo/fonts/Entypo.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'FontAwesome';
  src: url(${url(require('@react-native-vector-icons/fontawesome/fonts/FontAwesome.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'anticon';
  src: url(${url(require('@react-native-vector-icons/ant-design/fonts/AntDesign.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'zocial';
  src: url(${url(require('@react-native-vector-icons/zocial/fonts/Zocial.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'simple-line-icons';
  src: url(${url(require('@react-native-vector-icons/simple-line-icons/fonts/SimpleLineIcons.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'fontcustom';
  src: url(${url(require('@react-native-vector-icons/foundation/fonts/Foundation.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'FontAwesome6Free-Regular';
  src: url(${url(require('@react-native-vector-icons/fontawesome6/fonts/FontAwesome6_Regular.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'FontAwesome6Free-Solid';
  src: url(${url(require('@react-native-vector-icons/fontawesome6/fonts/FontAwesome6_Solid.ttf'))}) format('truetype');
}
@font-face {
  font-family: 'FontAwesome6Brands-Regular';
  src: url(${url(require('@react-native-vector-icons/fontawesome6/fonts/FontAwesome6_Brands.ttf'))}) format('truetype');
}
`;

  const styleEl = doc.createElement('style');
  styleEl.id = styleId;
  styleEl.type = 'text/css';
  styleEl.appendChild(doc.createTextNode(css));
  doc.head.appendChild(styleEl);
}

// ---------------------------------------------------------------------------
// OTA Updates — Hot Updater (native-only, web builds are unaffected)
// See docs/OTA-UPDATES.md for usage instructions.
// ---------------------------------------------------------------------------
let ExportedApp: React.ComponentType = App;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { HotUpdater } = require('@hot-updater/react-native');
    ExportedApp = HotUpdater.wrap({
      // TODO: Replace with your Cloudflare Worker URL after running `npx hot-updater init`
      baseURL: "https://hot-updater.luihugo247.workers.dev/api/check-update",
      updateStrategy: 'appVersion' as const,
      fallbackComponent: ({
        progress,
        status,
      }: {
        progress: number;
        status: string;
      }) => {
        const RN = require('react-native');
        return (
          <RN.View
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#1560bd',
            }}
          >
            <RN.ActivityIndicator size="large" color="#ffffff" />
            <RN.Text
              style={{
                color: '#ffffff',
                fontSize: 16,
                marginTop: 16,
                fontFamily: 'GT-Walsheim-Regular',
              }}
            >
              {status === 'UPDATING'
                ? `Updating... ${Math.round(progress * 100)}%`
                : 'Checking for updates...'}
            </RN.Text>
          </RN.View>
        );
      },
      onError: (error: Error) => {
        console.warn('[HotUpdater] Update error:', error);
      },
    })(App);
  } catch (e) {
    console.warn('[HotUpdater] Failed to initialize:', e);
  }
}

export default ExportedApp;
